import { toRefusal } from "../errors";
import { refusalNotice } from "./notice";
import type { MaintenanceNotice } from "./notice";

export const MIN_POLL_SECONDS = 15;
export const MAX_POLL_SECONDS = 120;
const DEFAULT_POLL_SECONDS = 30;

/** The cheap read the waiting screen asks. Any answer but 503 means the app is open again, even a 401. */
const PROBE_PATH = "/api/v1/auth/me";

/** How long to wait before asking again: the server's `Retry-After`, kept between 15 and 120 seconds. */
export function pollSeconds(retryAfter: number | undefined): number {
  return Math.min(MAX_POLL_SECONDS, Math.max(MIN_POLL_SECONDS, retryAfter ?? DEFAULT_POLL_SECONDS));
}

type WatchOptions = {
  readonly notice: MaintenanceNotice;
  /** Called when the hold changes (a new end time), so the screen can show it. */
  readonly onNotice: (notice: MaintenanceNotice) => void;
  /** Called once, when the answer is no longer 503. */
  readonly onReopened: () => void;
  readonly fetch?: typeof globalThis.fetch;
};

function probeUrl(notice: MaintenanceNotice): string {
  return new URL(PROBE_PATH, notice.url ?? globalThis.location?.href).href;
}

async function maintenanceAnswer(response: Response): Promise<MaintenanceNotice | undefined> {
  const body: unknown = await response.json().catch(() => undefined);
  return refusalNotice(toRefusal(response, body));
}

/**
 * Asks the app a cheap question every `Retry-After` seconds until it answers with anything but 503.
 * A failed ask (offline, still restarting) is not an answer: it waits and tries again. Returns a stop function.
 */
export function watchForReopening({ notice, onNotice, onReopened, fetch = globalThis.fetch.bind(globalThis) }: WatchOptions): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let current = notice;

  function schedule() {
    timer = setTimeout(ask, pollSeconds(current.retryAfter) * 1000);
  }

  async function ask() {
    try {
      const response = await fetch(probeUrl(current), { method: "GET", cache: "no-store" });
      if (stopped) return;
      if (response.status !== 503) return onReopened();
      const latest = await maintenanceAnswer(response);
      if (stopped) return;
      if (latest) {
        current = { ...latest, url: current.url };
        onNotice(current);
      }
    } catch {
      // Not an answer. Ask again on the next beat.
    }
    if (!stopped) schedule();
  }

  schedule();
  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
  };
}
